import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Bookmark } from 'lucide-react';
import { useLibrary } from '../lib/library';

const YOUTUBE = 'https://www.youtube.com/@AIRevolutionDigestPodcast';

const navLinks = [
  { label: 'Episodes', to: '/episodes' },
  { label: 'About', to: '/about' },
  { label: 'Apply to Speak', to: '/apply' },
];

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count: libCount } = useLibrary();

  const isActive = (to) => location.pathname === to;

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 glass"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 smooth hover:opacity-80" data-testid="logo-link">
            <img src="/logo.png" alt="AI Revolution Digest" className="h-10 w-10 sm:h-12 sm:w-12" />
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold tracking-tight">AI Revolution Digest</h1>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-sm font-medium smooth ${isActive(l.to) ? 'text-[#EF5B5B]' : 'text-gray-400 hover:text-white'}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/library"
              data-testid="nav-library"
              aria-label="My Library"
              className={`relative inline-flex items-center gap-1.5 text-sm font-medium smooth ${
                isActive('/library') ? 'text-[#EF5B5B]' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Bookmark className="w-4 h-4" fill={libCount > 0 ? 'currentColor' : 'none'} />
              <span>Library</span>
              {libCount > 0 && (
                <span
                  data-testid="nav-library-count"
                  className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#EF5B5B] text-white text-[10px] font-black inline-flex items-center justify-center shadow-[0_0_12px_rgba(239,91,91,0.6)]"
                >
                  {libCount}
                </span>
              )}
            </Link>
            <a
              href={YOUTUBE}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 btn-red rounded-full text-sm font-semibold"
            >
              Subscribe
            </a>
          </nav>

          {/* Mobile toggle */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full glass"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden mt-4 flex flex-col gap-3 pb-2"
          >
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className={`text-sm font-medium py-2 ${isActive(l.to) ? 'text-[#EF5B5B]' : 'text-gray-300'}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/library"
              onClick={() => setMobileOpen(false)}
              data-testid="nav-library-mobile"
              className={`inline-flex items-center gap-2 text-sm font-medium py-2 ${isActive('/library') ? 'text-[#EF5B5B]' : 'text-gray-300'}`}
            >
              <Bookmark className="w-4 h-4" fill={libCount > 0 ? 'currentColor' : 'none'} />
              My Library
              {libCount > 0 && (
                <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#EF5B5B] text-white text-[10px] font-black inline-flex items-center justify-center">
                  {libCount}
                </span>
              )}
            </Link>
            <a href={YOUTUBE} target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 btn-red rounded-full text-sm font-semibold text-center">
              Subscribe
            </a>
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
};

export default Header;
